import { CreateBetGameSettingType, ResReadBetGameSettingListType } from '@/types';
import { Box, Button } from '@mui/material';
import { useState, MouseEvent } from 'react';
import { z as zod } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Loading from '@/component/mui/Backdrop';
import BetGameSettingForm from '@/component/bet/BetGameSettingForm';
import { createBetGameSetting } from '@/api/bet/create';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const schema = zod.object({
  gameSettingId: zod.string().optional(),
  name: zod.string().min(1, { message: '컨트랙트 주소, 필수 입력' }),
  totAmount: zod.string().optional(),
  type: zod.number().min(1, { message: '사용자 지갑주소, 필수입력' }),
  minTotAmount: zod.string().min(1, { message: '사용자 지갑주소, 필수입력' }),
  jackpotAmount: zod.string().optional(),
  jackpotReady: zod.string().optional(),
  betLimitMax: zod.string().min(1, { message: '사용자 지갑주소, 필수입력' }),
  betLimitMin: zod.string().min(1, { message: '사용자 지갑주소, 필수입력' }),
  jackpotAmountRatio: zod.string().min(1, { message: '사용자 지갑주소, 필수입력' }),
  jackpotReadyRatio: zod.string().min(1, { message: '사용자 지갑주소, 필수입력' }),
  burnAmountRatio: zod.string().min(1, { message: '사용자 지갑주소, 필수입력' }),
  winMultiple: zod.string().min(1, { message: '사용자 지갑주소, 필수입력' }),
  regDt: zod.string().optional(),
  isActive: zod.number().min(1, { message: '사용자 지갑주소, 필수입력' }),
  inviteBetRatio: zod.string().min(1, { message: '피추천인 베팅금액 대비 리워드, 필수입력' }),
  inviteWinRatio: zod.string().min(1, { message: '피추천인 당첨금 대비 리워드, 필수입력' }),
  referralRatio: zod.string().min(1, { message: '레퍼럴 서비스 운용 비율, 필수입력' }),
});

type Values = zod.infer<typeof schema>;
const defaultValues = {
  gameSettingId: '',
  name: '',
  totAmount: '',
  type: -1,
  minTotAmount: '',
  jackpotAmount: '',
  jackpotReady: '',
  betLimitMax: '',
  betLimitMin: '',
  jackpotAmountRatio: '',
  jackpotReadyRatio: '',
  burnAmountRatio: '',
  regDt: '',
  isActive: 0,
  winMultiple: '',
  inviteBetRatio: '',
  inviteWinRatio: '',
  referralRatio: '',
} satisfies Values;

export default function BetGameSetting() {
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState<CreateBetGameSettingType | ResReadBetGameSettingListType>({
    gameSettingId: '',
    name: '',
    totAmount: '',
    type: 0,
    minTotAmount: '',
    jackpotAmount: '',
    jackpotReady: '',
    betLimitMax: '',
    betLimitMin: '',
    jackpotAmountRatio: '',
    jackpotReadyRatio: '',
    burnAmountRatio: '',
    regDt: '',
    isActive: 0,
    winMultiple: '',
    inviteBetRatio: '',
    inviteWinRatio: '',
    referralRatio: '',
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  const navigate = useNavigate();
  const onSubmit = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      // CreateBetGameSettingType에 필요한 필드들만 추출
      const createData: CreateBetGameSettingType = {
        name: settings.name,
        minTotAmount: settings.minTotAmount,
        betLimitMax: settings.betLimitMax,
        betLimitMin: settings.betLimitMin,
        jackpotAmountRatio: settings.jackpotAmountRatio,
        jackpotReadyRatio: settings.jackpotReadyRatio,
        burnAmountRatio: settings.burnAmountRatio,
        winMultiple: settings.winMultiple,
        type: settings.type,
        inviteBetRatio: settings.inviteBetRatio,
        inviteWinRatio: settings.inviteWinRatio,
        referralRatio: settings.referralRatio,
      };

      await createBetGameSetting(createData);
      toast.success('성공');
      navigate('/bet-game-setting-list');
    } catch (e) {
      console.log(e);
      toast.error('실패');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          overflow: 'auto',
          flex: 1,
          flexDirection: 'column',
          gap: { xs: '16px', sm: '0px' },
          padding: 2,
        }}
      >
        {settings && <BetGameSettingForm settings={settings} setSettings={setSettings} types="create" />}
        <Button variant="contained" onClick={onSubmit}>
          Create
        </Button>
      </Box>
      <Loading isLoading={isLoading} />
    </>
  );
}
